import { imgToBase64 } from "lib/utils/imgToBase64";
import { authFetch } from "lib/api/authFetch";
import { BACKEND_URL } from "lib/utils/baseUrl";
import { normalizeApiMessage } from "lib/i18n/apiMessage";

// HELPER 1 : API 메시지 정규화 + fallback messageKey 보정
function normalizeApiMessageWithFallbackKey(
  payload,
  fallbackMessage,
  fallbackKey,
) {
  const normalized = normalizeApiMessage(payload, fallbackMessage);
  return {
    ...normalized,
    messageKey: normalized.messageKey || fallbackKey || "",
  };
}

export async function postUpdateThermalInfo({
  refs = [],
  markupThermalDataUrl = null,
  markupTripleSpotDataUrl = null,
  project_id,
  upload_id,
  click_points,
  click_areas,
  circles,
  volume,
  avg_decibel,
  inspection_result,
  fusion_result,
  create_inspection_result,
  currentPaletteOptionNumber,
  locale = "",
}) {
  // STEP 1 : 필수 식별자(project_id, upload_id) 유효성 검증
  if (!project_id || !upload_id) {
    return {
      success: false,
      ...normalizeApiMessageWithFallbackKey(
        { message: "Invalid request." },
        "Invalid request.",
        "api.project.file.thermal.update.validation.invalidRequest",
      ),
    };
  }

  try {
    // STEP 2 : refs 기반 이미지들을 base64 데이터 URL 형태로 변환
    const dataUrls = refs.length > 0 ? await imgToBase64(refs) : [];
    const [thermalDataUrl, tripleSpotDataUrl] = Array.isArray(dataUrls)
      ? dataUrls
      : [];

    // STEP 3 : 직접 전달된 마크업 데이터와 refs 변환 결과를 우선순위에 따라 정리
    const resolvedThermalDataUrl = markupThermalDataUrl ?? thermalDataUrl;
    const resolvedTripleSpotDataUrl =
      markupTripleSpotDataUrl ?? tripleSpotDataUrl;

    // STEP 4 : 클릭 포인트는 click_points 우선, 없으면 circles를 fallback으로 사용
    const resolvedClickPoints = click_points ?? circles;
    const hasAnyClickPoints =
      Array.isArray(resolvedClickPoints) &&
      resolvedClickPoints.some((point) => Boolean(point));
    const shouldIncludeClickPoints =
      Array.isArray(click_points) || hasAnyClickPoints;

    // STEP 5 : 클릭 영역 데이터 존재 여부 확인
    const shouldIncludeClickAreas = Array.isArray(click_areas);

    // STEP 6 : 전송 가능한 변경 데이터가 전혀 없는 경우 요청 중단
    if (
      !resolvedThermalDataUrl &&
      !resolvedTripleSpotDataUrl &&
      !shouldIncludeClickPoints &&
      !shouldIncludeClickAreas
    ) {
      return {
        success: false,
        ...normalizeApiMessageWithFallbackKey(
          { message: "No update payload found." },
          "No update payload found.",
          "api.project.file.thermal.update.validation.emptyPayload",
        ),
      };
    }

    // STEP 7 : 서버 전송용 FormData 생성 및 기본 필드 구성
    const fd = new FormData();
    fd.append("upload_id", upload_id);

    if (
      currentPaletteOptionNumber !== undefined &&
      currentPaletteOptionNumber !== null &&
      currentPaletteOptionNumber !== ""
    ) {
      fd.append("thermal_palette", String(currentPaletteOptionNumber));
    }

    fd.append("avg_decibel", avg_decibel);
    fd.append("volume", volume);
    fd.append("inspection_result", inspection_result);
    fd.append("fusion_result", fusion_result);
    if (
      create_inspection_result !== undefined &&
      create_inspection_result !== null
    ) {
      fd.append(
        "create_inspection_result",
        create_inspection_result ? "true" : "false",
      );
    }

    // STEP 8 : 열화상 마크업 이미지가 있으면 File 객체로 변환 후 FormData에 추가
    if (resolvedThermalDataUrl) {
      const file = await dataUrlToFile(
        resolvedThermalDataUrl,
        `${upload_id}_thermal.png`,
      );
      fd.append("markup_thermal", file);
    }

    // STEP 9 : 3상 비교 마크업 이미지가 있으면 File 객체로 변환 후 FormData에 추가
    if (resolvedTripleSpotDataUrl) {
      const file = await dataUrlToFile(
        resolvedTripleSpotDataUrl,
        `${upload_id}_triplespot.png`,
      );
      fd.append("markup_triplespot", file);
    }

    // STEP 10 : 클릭 포인트 데이터가 있으면 JSON 문자열로 직렬화하여 추가
    if (shouldIncludeClickPoints && Array.isArray(resolvedClickPoints)) {
      fd.append("click_points", JSON.stringify(resolvedClickPoints));
    }

    // STEP 11 : 클릭 영역 데이터가 있는 경우 JSON 문자열로 직렬화하여 추가
    if (shouldIncludeClickAreas) {
      fd.append("click_areas", JSON.stringify(click_areas));
    }

    // STEP 12 : locale 파라미터를 반영하여 요청 endpoint 구성
    const localeQuery = locale ? `?locale=${encodeURIComponent(locale)}` : "";
    const endpoint = `${BACKEND_URL}/project/${project_id}/thermal_info/${upload_id}${localeQuery}`;

    // STEP 13 : 인증 포함 POST 요청 전송
    const res = await authFetch(
      endpoint,
      {
        method: "POST",
        body: fd,
      },
      {
        refreshUrlIncludes: "auth/refresh",
      },
    );

    // STEP 14 : 서버 응답 JSON 파싱
    const json = await res.json().catch(() => null);
    if (!json) {
      return {
        success: false,
        ...normalizeApiMessageWithFallbackKey(
          { message: "Failed to parse server response." },
          "Failed to parse server response.",
          "api.common.serverError",
        ),
      };
    }

    // STEP 15 : 비정상 응답 시 상태코드 기준 fallback messageKey 결정 후 실패 반환
    if (!res.ok || json?.success !== true) {
      const fallbackKey =
        res.status === 401
          ? "api.auth.token.expired"
          : res.status === 404
            ? "api.project.file.thermal.update.notFound"
            : res.status === 400
              ? "api.project.file.thermal.update.validation.invalidRequest"
              : "api.project.file.thermal.update.failed";

      return {
        success: false,
        ...normalizeApiMessageWithFallbackKey(
          json,
          "Thermal info update failed.",
          fallbackKey,
        ),
      };
    }

    // STEP 16 : 정상 응답 시 성공 메시지와 함께 반환
    return {
      success: true,
      ...normalizeApiMessageWithFallbackKey(
        json,
        "Thermal info update completed.",
        "api.project.file.thermal.update.success",
      ),
    };
  } catch (error) {
    // STEP 17 : 네트워크 또는 런타임 예외 처리
    console.error("updateThermalInfo failed:", error);
    return {
      success: false,
      ...normalizeApiMessageWithFallbackKey(
        { message: "Update request failed." },
        "Update request failed.",
        "api.common.networkError",
      ),
      error: String(error),
    };
  }
}

// HELPER 2 : data URL을 fetch 후 Blob으로 변환하여 File 객체 생성
async function dataUrlToFile(dataUrl, filename) {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], filename, { type: blob.type || "image/png" });
}
